import { useMemo } from 'react';
import type { EntitySuggestion, ProcessWithSteps } from '@bliks-io/contracts';

/**
 * Deduped union of every EntitySuggestion referenced by a published process,
 * aggregated from two sources:
 *  - each step recording's denormalized `entitySuggestions[]` (suggestions
 *    raised inside a recorded step), and
 *  - the response-level `entitySuggestions` carried by `/show-process` (the
 *    complete referenced set — open-assignment placeholders plus every
 *    suggestion-typed assignee/helper/successor/rule ref across the recordings,
 *    including a START-anchored proposed assignee that lives in no recording's
 *    per-step list).
 *
 * Threaded into `ProcessFlowVisualization` as its single `entitySuggestions`
 * array: the canvas builder derives its proposed-name/status map from it, and
 * the step-detail sidebar unions it with each step's own `entitySuggestions` so
 * suggestion refs resolve to a proposed name (blue pending, red declined) on the
 * published surfaces exactly like the editor.
 */
export function useProcessEntitySuggestions(
  process: ProcessWithSteps | null | undefined,
  entitySuggestions?: readonly EntitySuggestion[] | null
): EntitySuggestion[] {
  return useMemo(() => {
    const byId = new Map<string, EntitySuggestion>();
    for (const step of process?.processStepRecordings ?? []) {
      for (const s of step.entitySuggestions ?? []) byId.set(String(s._id), s);
    }
    for (const s of entitySuggestions ?? []) byId.set(String(s._id), s);
    return [...byId.values()];
  }, [process, entitySuggestions]);
}
